import React, { useState } from 'react'
import { X, Gift, CreditCard, Mail, User, Sparkles, Crown, Zap } from 'lucide-react'
import { useAuth } from '../../context/AuthContext'
import GiftService from '../../services/gifts'

interface GiftModalProps {
  isOpen: boolean
  onClose: () => void
}

interface GiftOption {
  id: string
  type: 'subscription' | 'credits'
  title: string
  description: string
  price: number
  value: string
  duration?: string
  popular?: boolean
  icon: React.ReactNode
  features: string[]
}

const GiftModal: React.FC<GiftModalProps> = ({ isOpen, onClose }) => {
  const { user } = useAuth()
  const [selectedGift, setSelectedGift] = useState<string>('')
  const [recipientEmail, setRecipientEmail] = useState('')
  const [giftMessage, setGiftMessage] = useState('')
  const [senderName, setSenderName] = useState('')
  const [isProcessing, setIsProcessing] = useState(false)
  const [step, setStep] = useState<'select' | 'details' | 'payment' | 'success'>('select')

  const giftOptions: GiftOption[] = [
    {
      id: 'credits_50',
      type: 'credits',
      title: '50 Image Credits',
      description: 'Perfect for trying out premium features',
      price: 4.99,
      value: '50 generations',
      icon: <Zap className="w-6 h-6 text-yellow-500" />,
      features: [
        '50 high-quality AI generations',
        'Access to all filters and styles',
        'No expiration date',
        'Instant delivery'
      ]
    },
    {
      id: 'subscription_1',
      type: 'subscription',
      title: '1 Month Premium',
      description: 'Get started with premium features',
      price: 9.99,
      value: '1 month',
      duration: '30 days',
      icon: <Crown className="w-6 h-6 text-blue-500" />,
      features: [
        'Unlimited AI generations',
        'Priority processing',
        'Advanced filters & styles',
        'HD quality images',
        'Early access to new features'
      ]
    },
    {
      id: 'subscription_3',
      type: 'subscription',
      title: '3 Month Premium',
      description: 'Great value for regular users',
      price: 24.99,
      value: '3 months',
      duration: '90 days',
      popular: true,
      icon: <Crown className="w-6 h-6 text-purple-500" />,
      features: [
        'Unlimited AI generations',
        'Priority processing',
        'Advanced filters & styles',
        'HD quality images',
        'Early access to new features',
        '17% savings vs monthly'
      ]
    },
    {
      id: 'subscription_6',
      type: 'subscription',
      title: '6 Month Premium',
      description: 'Best for dedicated enthusiasts',
      price: 44.99,
      value: '6 months',
      duration: '180 days',
      icon: <Crown className="w-6 h-6 text-pink-500" />,
      features: [
        'Unlimited AI generations',
        'Priority processing',
        'Advanced filters & styles',
        'HD quality images',
        'Early access to new features',
        '25% savings vs monthly'
      ]
    },
    {
      id: 'subscription_12',
      type: 'subscription',
      title: '1 Year Premium',
      description: 'Maximum value and features',
      price: 79.99,
      value: '12 months',
      duration: '365 days',
      icon: <Crown className="w-6 h-6 text-gold-500" />,
      features: [
        'Unlimited AI generations',
        'Priority processing',
        'Advanced filters & styles',
        'HD quality images',
        'Early access to new features',
        'Exclusive beta features',
        '33% savings vs monthly'
      ]
    }
  ]

  const selectedOption = giftOptions.find(option => option.id === selectedGift)

  const handleGiftSelection = (giftId: string) => {
    setSelectedGift(giftId)
    setStep('details')
  }

  const handleDetailsSubmit = () => {
    if (!recipientEmail || !senderName) return
    setStep('payment')
  }

  const handlePayment = async () => {
    if (!selectedOption) return
    
    setIsProcessing(true)
    
    try {
      // Create gift purchase data
      const giftData = {
        giftType: selectedOption.type,
        giftId: selectedOption.id,
        title: selectedOption.title,
        description: selectedOption.description,
        price: selectedOption.price,
        valueAmount: selectedOption.type === 'credits' ? 50 : parseInt(selectedOption.value.split(' ')[0]),
        purchaserName: senderName,
        recipientEmail,
        giftMessage
      }

      // Create payment intent through gift service
      const { clientSecret, giftId } = await GiftService.purchaseGift(giftData)
      
      // Process payment with Stripe (simulated for demo)
      const paymentResult = await GiftService.processStripePayment(clientSecret, {
        email: user?.email || '',
        name: senderName
      })

      if (paymentResult.success) {
        // Confirm payment with backend
        await GiftService.confirmGiftPayment(clientSecret.split('_secret_')[0], 'succeeded')
        
        // Send gift email
        await GiftService.sendGiftEmail(giftId)
        
        setStep('success')
      } else {
        throw new Error(paymentResult.error || 'Payment failed')
      }
    } catch (error) {
      console.error('Payment failed:', error)
      // Handle payment error - show error message to user
      alert(error instanceof Error ? error.message : 'Payment failed. Please try again.')
    } finally {
      setIsProcessing(false)
    }
  }

  const resetModal = () => {
    setStep('select')
    setSelectedGift('')
    setRecipientEmail('')
    setGiftMessage('')
    setSenderName('')
    setIsProcessing(false)
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-pink-500 to-purple-500 rounded-full flex items-center justify-center">
              <Gift className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Gift Toefetti Premium</h2>
              <p className="text-sm text-gray-600">Share the joy of AI-generated content</p>
            </div>
          </div>
          <button 
            onClick={() => {
              resetModal()
              onClose()
            }}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {step === 'select' && (
            <div>
              <div className="text-center mb-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Choose a Gift</h3>
                <p className="text-gray-600">Select the perfect gift for someone special</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {giftOptions.map((option) => (
                  <div
                    key={option.id}
                    onClick={() => handleGiftSelection(option.id)}
                    className={`relative p-6 border-2 rounded-xl cursor-pointer transition-all hover:shadow-lg ${
                      option.popular 
                        ? 'border-purple-500 bg-purple-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {option.popular && (
                      <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                        <span className="bg-purple-500 text-white px-3 py-1 rounded-full text-xs font-medium">
                          Most Popular
                        </span>
                      </div>
                    )}

                    <div className="text-center">
                      <div className="mb-4">
                        {option.icon}
                      </div>
                      
                      <h4 className="text-lg font-semibold text-gray-900 mb-2">
                        {option.title}
                      </h4>
                      
                      <p className="text-sm text-gray-600 mb-4">
                        {option.description}
                      </p>
                      
                      <div className="mb-4">
                        <span className="text-3xl font-bold text-gray-900">
                          ${option.price}
                        </span>
                        {option.duration && (
                          <span className="text-sm text-gray-500 ml-1">
                            for {option.value}
                          </span>
                        )}
                      </div>

                      <ul className="text-xs text-gray-600 space-y-1 mb-4">
                        {option.features.map((feature, index) => (
                          <li key={index} className="flex items-center">
                            <Sparkles className="w-3 h-3 text-green-500 mr-1 flex-shrink-0" />
                            {feature}
                          </li>
                        ))}
                      </ul>

                      <button className={`w-full py-2 px-4 rounded-lg font-medium transition-colors ${
                        option.popular
                          ? 'bg-purple-500 hover:bg-purple-600 text-white'
                          : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
                      }`}>
                        Select Gift
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {step === 'details' && selectedOption && (
            <div className="max-w-2xl mx-auto">
              <div className="text-center mb-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Gift Details</h3>
                <p className="text-gray-600">Who are you gifting this to?</p>
              </div>

              {/* Selected Gift Summary */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="flex items-center space-x-3">
                  {selectedOption.icon}
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900">{selectedOption.title}</h4>
                    <p className="text-sm text-gray-600">${selectedOption.price}</p>
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                {/* Recipient Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Mail className="w-4 h-4 inline mr-1" />
                    Recipient's Email
                  </label>
                  <input
                    type="email"
                    value={recipientEmail}
                    onChange={(e) => setRecipientEmail(e.target.value)}
                    placeholder="Enter recipient's email address"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    required
                  />
                </div>

                {/* Sender Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <User className="w-4 h-4 inline mr-1" />
                    Your Name
                  </label>
                  <input
                    type="text"
                    value={senderName}
                    onChange={(e) => setSenderName(e.target.value)}
                    placeholder="Enter your name"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    required
                  />
                </div>

                {/* Gift Message */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Personal Message (Optional)
                  </label>
                  <textarea
                    value={giftMessage}
                    onChange={(e) => setGiftMessage(e.target.value)}
                    placeholder="Add a personal message to your gift..."
                    rows={4}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex space-x-4">
                  <button
                    onClick={() => setStep('select')}
                    className="flex-1 py-3 px-6 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Back
                  </button>
                  <button
                    onClick={handleDetailsSubmit}
                    disabled={!recipientEmail || !senderName}
                    className="flex-1 py-3 px-6 bg-purple-500 hover:bg-purple-600 disabled:bg-gray-300 text-white rounded-lg transition-colors"
                  >
                    Continue to Payment
                  </button>
                </div>
              </div>
            </div>
          )}

          {step === 'payment' && selectedOption && (
            <div className="max-w-2xl mx-auto">
              <div className="text-center mb-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Complete Your Gift</h3>
                <p className="text-gray-600">Review and pay for your gift</p>
              </div>

              {/* Order Summary */}
              <div className="bg-gray-50 rounded-lg p-6 mb-6">
                <h4 className="font-semibold text-gray-900 mb-4">Order Summary</h4>
                
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Gift:</span>
                    <span className="font-medium">{selectedOption.title}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Recipient:</span>
                    <span className="font-medium">{recipientEmail}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">From:</span>
                    <span className="font-medium">{senderName}</span>
                  </div>
                  <div className="border-t pt-3 flex justify-between text-lg font-semibold">
                    <span>Total:</span>
                    <span>${selectedOption.price}</span>
                  </div>
                </div>
              </div>

              {/* Payment Section */}
              <div className="bg-white border-2 border-gray-200 rounded-lg p-6 mb-6">
                <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                  <CreditCard className="w-5 h-5 mr-2" />
                  Payment Information
                </h4>
                
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CreditCard className="w-8 h-8 text-blue-500" />
                  </div>
                  <p className="text-gray-600 mb-4">
                    Secure payment processing powered by Stripe
                  </p>
                  <p className="text-sm text-gray-500">
                    Your payment information is encrypted and secure
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-4">
                <button
                  onClick={() => setStep('details')}
                  className="flex-1 py-3 px-6 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={handlePayment}
                  disabled={isProcessing}
                  className="flex-1 py-3 px-6 bg-purple-500 hover:bg-purple-600 disabled:bg-gray-300 text-white rounded-lg transition-colors flex items-center justify-center"
                >
                  {isProcessing ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin mr-2" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5 mr-2" />
                      Complete Purchase
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {step === 'success' && selectedOption && (
            <div className="max-w-2xl mx-auto text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Gift className="w-10 h-10 text-green-500" />
              </div>
              
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Gift Sent Successfully! 🎉
              </h3>
              
              <p className="text-gray-600 mb-6">
                Your gift of <strong>{selectedOption.title}</strong> has been sent to{' '}
                <strong>{recipientEmail}</strong>. They'll receive an email with instructions 
                on how to redeem their gift.
              </p>

              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <p className="text-green-700 text-sm">
                  <strong>What happens next:</strong><br />
                  • The recipient will receive an email with their gift code<br />
                  • They can redeem it by creating an account or logging in<br />
                  • The {selectedOption.type === 'credits' ? 'credits' : 'subscription'} will be applied immediately
                </p>
              </div>

              <button
                onClick={() => {
                  resetModal()
                  onClose()
                }}
                className="py-3 px-8 bg-purple-500 hover:bg-purple-600 text-white rounded-lg transition-colors"
              >
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default GiftModal 